import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { canonicalFor, usePageMeta } from '../../hooks/usePageMeta';
import { PageHeader } from '../../components/page/PageHeader';
import { ContentSection } from '../../components/page/ContentSection';
import { CatalogTable } from '../../components/page/CatalogTable';
import { SalesCallout } from '../../components/page/SalesCallout';
import { RelatedLinks } from '../../components/page/RelatedLinks';
import { catalog, CatalogCategory, categoryMeta } from '../../data/catalog';
const categoryNotes: Record<CatalogCategory, string> = {
  kits: 'A holder, a box of APGX 331R inserts, a Torx driver and extra screws for one shank size.',
  holders: 'External turning holders in 1-inch and 3/4-inch shank sizes.',
  inserts: 'APGX inserts in MG12 roughing, MG12 -30R chipbreaker and DR80 honed, MAX-coated versions.',
  accessories: 'The Torx driver and low-profile insert screw used for service.'
};
const order: CatalogCategory[] = ['kits', 'holders', 'inserts', 'accessories'];
export function ProductsOverview() {
  usePageMeta({
    title: 'Shear Geometry Products: Holders, APGX Inserts & Kits | Robertson Precision',
    description: 'The Shear Geometry catalog: SALXR 16-3D and 12-3B holders, APGX MG12 and DR80 inserts, starter kits and service parts. Call 800-397-7745 for pricing and availability.',
    canonical: canonicalFor('/products/')
  });
  return <>
      <PageHeader eyebrow="Products" title="Shear Geometry Products" lead="Shear Geometry tooling is made in the USA. Browse external tool holders, replaceable APGX inserts, starter kits and service parts for CNC lathes. Start with the shank size that fits your turret, then choose the insert for the material and chip-control goal." crumbs={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Products'
    }]} data-id="element-802" data-mp="2l9m2f91w0n7h" />

      <ContentSection id="categories" title="Browse by category" data-id="element-803" data-mp="14xgo7em50tpd">
        <ul className="grid gap-px border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4" data-id="element-804" data-mp="37jvs1thdtk38">
          {order.map(cat => {
          const count = catalog.filter(c => c.category === cat).length;
          return <li key={cat} className="flex bg-white" data-id="element-805" data-mp="2xtalnjvgvb9r">
                <Link to={categoryMeta[cat].href} data-analytics="product-category-click" data-analytics-label={categoryMeta[cat].label} className="group flex w-full flex-col p-6 transition-colors duration-150 ease-precise hover:bg-warm" data-id="element-806" data-mp="2xrzkzsxbijmv">
                  <span className="font-mono text-[10px] tracking-label text-copper" data-id="element-807" data-mp="2euye22k7e9ga">
                    {count} {count === 1 ? 'PART' : 'PARTS'}
                  </span>
                  <span className="mt-3 font-display text-[19px] font-medium text-navy group-hover:text-copper" data-id="element-808" data-mp="29yg9b2ovib3y">
                    {categoryMeta[cat].label}
                  </span>
                  <span className="mt-2 text-[14px] leading-relaxed text-graphite/80" data-id="element-809" data-mp="85jmyjpijm4j">
                    {categoryNotes[cat]}
                  </span>
                  <span className="mt-auto inline-flex items-center gap-2 pt-5 text-[13.5px] font-semibold text-navy" data-id="element-810" data-mp="kmkptbebbezw">
                    View parts
                    <ArrowRightIcon aria-hidden="true" className="h-4 w-4 text-copper" data-id="element-811" data-mp="pg5qjb7ain2s" />
                  </span>
                </Link>
              </li>;
        })}
        </ul>
      </ContentSection>

      <ContentSection id="catalog" tone="warm" title="Full catalog" intro={<p data-id="element-813" data-mp="1qbdasj26wi0">All 13 Shear Geometry parts. Inserts are sold in full boxes of 10.</p>} data-id="element-812" data-mp="3sx5ubzu5sbkq">
        <CatalogTable rows={catalog} caption="Shear Geometry catalog: part numbers and descriptions" variant="overview" data-id="element-814" data-mp="6po2hjxekfnr" />
        <div className="mt-8" data-id="element-815" data-mp="1i1owped1cwaw">
          <SalesCallout location="products-overview" data-id="element-816" data-mp="1i4ujuo23to3z" />
        </div>
      </ContentSection>

      <RelatedLinks links={[{
      label: 'Applications',
      href: '/applications/',
      description: 'Match inserts to 6061-T6 aluminum, copper or titanium.'
    }, {
      label: 'Tool Selection',
      href: '/technical-resources/tool-selection/',
      description: 'Holder, insert and application in three steps.'
    }, {
      label: 'Starting Data',
      href: '/technical-resources/starting-data/',
      description: 'Published starting guidance by material.'
    }, {
      label: 'Contact',
      href: '/contact/',
      description: 'Sales and technical assistance lines.'
    }]} data-id="element-817" data-mp="1n004ljjrswqh" />
    </>;
}